import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { safeRedirectPath } from '@mockd/shared';
import { useAuth } from '../hooks';
import { ROUTES } from '../routes';
import { Alert, GitHubMark, MockdLogo } from '../components/common';
import { MagicLinkForm, MagicLinkSent } from '../components/auth';

export function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loading, error, clearError, loginWithGitHub, magicLinkSent, resetMagicLink } = useAuth();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'magic' | 'password'>('magic');
  const [validationError, setValidationError] = useState<string | null>(null);

  const redirect = searchParams.get('redirect') ?? undefined;
  const from = safeRedirectPath(redirect) ?? '/';

  // Clear any stale sent-state from a previous visit so the form is visible again
  useEffect(() => {
    resetMagicLink();
  }, [resetMagicLink]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError(null);

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    try {
      await register({ email, password });
      navigate(from, { replace: true });
    } catch {
      // Error is handled by useAuth
    }
  };

  const toggleMode = () => {
    clearError();
    setValidationError(null);
    setMode(mode === 'magic' ? 'password' : 'magic');
  };

  const displayError = validationError || error;

  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <MockdLogo size="md" className="mx-auto" />
          <h1 className="mt-4 text-base-content">Create your account</h1>
        </div>

        <div className="p-6">
          {magicLinkSent ? (
            <MagicLinkSent linkLabel="sign-up link" />
          ) : (
            <>
              {displayError && (
                <Alert variant="error" className="mb-4">{displayError}</Alert>
              )}

              {/* GitHub OAuth Button */}
              <button
                onClick={() => loginWithGitHub(safeRedirectPath(redirect) ?? undefined)}
                disabled={loading}
                className="btn btn-block btn-outline"
              >
                <GitHubMark />
                Continue with GitHub
              </button>

              <div className="divider">or</div>

              {mode === 'magic' ? (
                <MagicLinkForm
                  email={email}
                  onEmailChange={setEmail}
                  buttonLabel="Send sign-up link"
                  onBeforeSubmit={() => setValidationError(null)}
                  redirect={safeRedirectPath(redirect) ?? undefined}
                />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="fieldset">
                    <label htmlFor="email" className="fieldset-legend">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="input w-full"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="fieldset">
                    <label htmlFor="password" className="fieldset-legend">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="input w-full pr-10"
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary w-full"
                    >
                      {loading ? 'Creating account...' : 'Create account'}
                    </button>
                  </div>
                </form>
              )}

              <div className="mt-4 text-center text-sm">
                <button onClick={toggleMode} className="link link-primary">
                  {mode === 'magic' ? 'Use password instead' : 'Use a magic link instead'}
                </button>
              </div>

              <div className="mt-4 text-center text-sm text-base-content/80">
                Already have an account?{' '}
                <Link
                  to={{ pathname: ROUTES.auth.login(), search: searchParams.toString() }}
                  className="link link-primary"
                >
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="link link-hover text-sm text-base-content/80">
            Continue without signing in
          </Link>
        </div>
      </div>
    </div>
  );
}
